import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { setSelectedAuthMethod, selectSelectedAuthMethod } from '../reducers/onboardingSlice';
import AuthenticationMethodSelector, {
  AUTHENTICATION_OPTIONS,
  AuthenticationOption,
} from './AuthenticationMethodSelector';
import ManagedKeyForm from './ManagedKeyForm';
import BYOKForm from './BYOKForm';
import PasswordAuthForm from './PasswordAuthForm';

/**
 * SnowflakeAccountFormV2 Component
 *
 * Orchestrator component that manages authentication method selection and
 * renders the appropriate form component based on user selection.
 *
 * Features:
 * - Authentication method selector with three options
 * - Conditional rendering of appropriate form component
 * - Integration with Redux for state management
 *
 * @component
 * @example
 * ```tsx
 * <SnowflakeAccountFormV2 />
 * ```
 */
const SnowflakeAccountFormV2: React.FC = () => {
  const dispatch = useAppDispatch();
  const selectedAuthMethodType = useAppSelector(selectSelectedAuthMethod);

  // Find the selected option object from AUTHENTICATION_OPTIONS
  const getSelectedOption = (): AuthenticationOption => {
    return AUTHENTICATION_OPTIONS.find((opt) => opt.value === selectedAuthMethodType) ?? AUTHENTICATION_OPTIONS[0];
  };

  const [selectedOption, setSelectedOption] = useState<AuthenticationOption>(getSelectedOption());

  // Sync local state with Redux state changes
  useEffect(() => {
    const newSelectedOption = getSelectedOption();
    if (newSelectedOption.value !== selectedOption.value) {
      setSelectedOption(newSelectedOption);
    }
  }, [selectedAuthMethodType]);

  /**
   * Handles authentication method change
   */
  const handleAuthMethodChange = (option: AuthenticationOption): void => {
    setSelectedOption(option);
    dispatch(setSelectedAuthMethod(option.value));
  };

  /**
   * Renders the appropriate form based on selected authentication method
   */
  const renderForm = (): React.ReactNode => {
    switch (selectedOption.value) {
      case 'managed_key':
        return <ManagedKeyForm />;
      case 'pkey':
        return <BYOKForm />;
      case 'password':
        return <PasswordAuthForm />;
      default:
        return <ManagedKeyForm />;
    }
  };

  return (
    <div className="space-y-8">
      {/* Authentication Method Selector */}
      <AuthenticationMethodSelector selectedAuthMethod={selectedOption} onChange={handleAuthMethodChange} />

      {/* Divider */}
      <div className="border-t border-gray-200" />

      {/* Selected Form */}
      <div>{renderForm()}</div>
    </div>
  );
};

export default SnowflakeAccountFormV2;
